import api from '../api';

export type CccdTeamStatus = 'draft' | 'negotiating' | 'payment_pending' | 'active' | 'inactive' | 'rejected';
export type DiscountType = 'none' | 'multi_team' | 'historical' | 'scholarship' | 'custom';
export type PaymentMethod = 'wompi' | 'bank_transfer_national' | 'bank_transfer_international';

// puntos-sponsors-and-live-cache: patrocinadores de un evento, mostrados en las
// pantallas públicas de puntos.
export type SponsorTier = 'principal' | 'aliado' | 'apoyo';

export interface EventSponsor {
  id: string;
  eventId: string;
  name: string;
  logoKey: string | null;
  /** Ruta pública estable del logo; null mientras no se haya subido uno. */
  logoUrl: string | null;
  url: string | null;
  tier: SponsorTier;
  sortOrder: number;
  isActive: boolean;
}

export interface EventSponsorInput {
  name: string;
  url: string | null;
  tier: SponsorTier;
  sortOrder: number;
}

export const MAX_ACTIVE_SPONSORS = 30;

export interface AdminTeamListItem {
  id: string;
  name: string;
  categoryId: string | null;
  planId: string;
  scope: 'national' | 'regional';
  status: CccdTeamStatus;
  discountType: DiscountType;
  discountPercent: number;
  createdAt: string;
  activatedAt: string | null;
  approvalExpiresAt: string | null;
  event: { id: string; name: string } | null;
  regionalEvent: { id: string; name: string } | null;
  // Opcional (cccd-team-optional-captain): un equipo admin-creado puede no
  // tener responsable todavía — se elige después desde Miembros.
  captain: { id: string; name: string; email: string } | null;
  _count: { members: number; payments: number };
}

// cccd-teams-institutions-csv-import
export type BulkCreateTeamRow = {
  line: number;
  name: string;
  categoryId?: string;
  planId?: string;
  institutionName?: string;
  captainEmail?: string;
  captainName?: string;
  discountType?: DiscountType;
  discountPercent?: number;
  initialStatus?: 'draft' | 'negotiating' | 'payment_pending' | 'active';
};

export type BulkCreateTeamsResult = {
  created: { id: string; name: string }[];
  skipped: { line: number; error: string }[];
};

export interface AdminTeamDetail extends Omit<AdminTeamListItem, '_count'> {
  // cccd-team-seedbed-roster-sync: cupo de la categoría del equipo — null =
  // sin límite.
  maxTeamSize: number | null;
  // cccd-public-teams-page: el equipo aparece en cccd.space/equipos. Ausente = false.
  publicProfileEnabled?: boolean;
  negotiationNotes: string | null;
  rejectedReason: string | null;
  institutionId: string | null;
  institution: { id: string; name: string } | null;
  approvedBy: { id: string; name: string } | null;
  members: AdminTeamMember[];
  payments: AdminPayment[];
  statusLogs: AdminStatusLog[];
  // cccd-team-showcase: el parche es el emblema/logo del equipo; la foto
  // grupal es una fotografía real de todos los integrantes juntos — ambos se
  // muestran en la galería competitiva de Academy, son conceptos distintos.
  patchImageKey: string | null;
  patchImageUrl: string | null;
  groupPhotoImageKey: string | null;
  groupPhotoUrl: string | null;
  motor: string | null;
  formsStatus: string | null;
  publicacion: boolean | null;
  fotosRrss: string | null;
  circular: boolean | null;
  plataforma: boolean | null;
  kitStatus: string | null;
  altimetroStatus: string | null;
  inscripcionStatus: string | null;
  estadoOperativo: string | null;
  pais: string | null;
  municipioDepartamento: string | null;
  // cccd-team-rename-seedbed-sync: null si el equipo nunca resolvió un
  // semillero; si existe, indica si ya tiene un código de acceso grupal
  // activo (para advertir antes de renombrar).
  seedbedGroup: { id: string; name: string; hasActiveCredential: boolean } | null;
  // Solo presente en la respuesta de PATCH /teams/:id cuando el rename
  // sincronizó un grupo vinculado (ver updateTeam).
  seedbedSync?: { synced: true; groupId: string; credentialRegenerated: boolean };
  // cccd-team-category-change: solo presente en la respuesta de PATCH
  // /teams/:id cuando categoryId cambió — advertencias no bloqueantes si el
  // equipo ya no cumple addons/motor/maxTeamSize de la categoría nueva.
  categoryChangeWarnings?: string[];
}

// cccd-team-member-student-link: agregar un miembro ya conocido crea la
// relación de inmediato — todos los campos de la ficha de intake (menos
// email, userId, id) pueden llegar `null` hasta que se completen después
// desde el perfil del estudiante (/instructor/students/:id/perfil), la
// única ficha editable — nunca desde una página aparte en CCCD.
export interface AdminTeamMember {
  id: string;
  userId: string | null;
  firstName: string | null;
  lastName: string | null;
  email: string;
  phone: string | null;
  role: string;
  inviteStatus: string;
  country: string | null;
  documentType: string | null;
  documentNumber: string | null;
  birthDate: string | null;
  gender: string | null;
  address: string | null;
  stateProvince: string | null;
  city: string | null;
  bloodType: string | null;
  disability: string | null;
  medications: string | null;
  guardianName: string | null;
  guardianRelationship: string | null;
  guardianDocumentType: string | null;
  guardianDocumentNumber: string | null;
  guardianPhone: string | null;
  guardianEmail: string | null;
  institution: string | null;
  selfDeclaredRole: string | null;
  imageAuthorization: boolean;
  dataTreatmentAuthorization: boolean;
  isPrimaryContact: boolean;
  // cccd-team-showcase: foto del integrante para la galería competitiva de
  // Academy — solo se muestra ahí si imageAuthorization es true.
  photoImageKey: string | null;
  photoImageUrl: string | null;
  // cccd-public-teams-page: consentimiento específico para publicar la foto en
  // cccd.space/equipos; independiente de imageAuthorization. Ausente = false.
  publicPhotoAuthorized?: boolean;
}

export interface AdminAddMemberInput {
  country?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  documentType?: string | null;
  documentNumber?: string | null;
  birthDate?: string | null; // YYYY-MM-DD
  gender?: string | null;
  email: string;
  phone?: string | null;
  address?: string | null;
  stateProvince?: string | null;
  city?: string | null;
  bloodType?: string | null;
  disability?: string | null;
  medications?: string | null;
  guardianName?: string | null;
  guardianRelationship?: string | null;
  guardianDocumentType?: string | null;
  guardianDocumentNumber?: string | null;
  guardianPhone?: string | null;
  guardianEmail?: string | null;
  institution?: string | null;
  selfDeclaredRole?: string | null;
  imageAuthorization?: boolean;
  dataTreatmentAuthorization?: boolean;
  // cccd-team-member-student-link: correlación opcional y mutuamente
  // excluyente con un estudiante ya conocido del ecosistema, encontrada por
  // searchStudents().
  userId?: string;
  pendingMemberId?: string;
}

// PATCH /teams/:teamId/members/:memberId — completar la ficha de un miembro
// ya agregado. Mismos campos que el alta, sin email/userId/pendingMemberId.
export type UpdateMemberInput = Omit<AdminAddMemberInput, 'email' | 'userId' | 'pendingMemberId'>;

// El `StudentProfile` de la cuenta (llenado en /instructor/students/:id/perfil)
// cuando cubre todo lo que el alta de CCCD exige — documento, dirección,
// salud y acudiente viven ahí, no en ningún otro lado.
export type PreviousMemberFields = UpdateMemberInput;

// GET /cccd/admin/students/search?query=... — resultados en vivo, tipo
// "invitar colaborador" de GitHub: un estudiante ya conocido del ecosistema
// (cccd-team-member-student-link).
export interface StudentCandidate {
  kind: 'user' | 'pending';
  userId?: string;
  pendingMemberId?: string;
  name: string;
  email: string;
  phone: string | null;
  previousMember: PreviousMemberFields | null;
}

export interface CccdAddon {
  id: string;
  name: string;
  priceCop: number;
  priceUsd: number;
  isActive: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
  _count: { categoryLinks: number };
}

export interface CategoryAddonLink {
  id: string;
  eventCategoryId: string;
  addonId: string;
  isDefault: boolean;
  addon: CccdAddon;
}

// Catálogo de construcción/decoración (cccd-team-supply-catalog) — entidad
// separada de CccdAddon: nunca se cobra, solo alimenta CccdSupplyRequest.
export type CatalogItemCategory = 'structure' | 'electronics' | 'launch_equipment' | 'merchandising';

export interface CccdCatalogItem {
  id: string;
  name: string;
  description: string;
  imageUrl: string | null;
  priceCop: number;
  category: CatalogItemCategory;
  isActive: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
  _count: { eventLinks: number };
}

export interface EventCatalogItemLink {
  id: string;
  eventId: string | null;
  regionalId: string | null;
  catalogItemId: string;
  catalogItem: CccdCatalogItem;
}

export interface CccdSupplyRequestItem {
  id: string;
  catalogItemId: string;
  quantity: number;
  catalogItem: CccdCatalogItem;
}

export interface CccdSupplyRequest {
  id: string;
  teamId: string;
  eventId: string | null;
  regionalEventId: string | null;
  status: 'pending' | 'reviewed';
  createdAt: string;
  updatedAt: string;
  team: { id: string; name: string };
  items: CccdSupplyRequestItem[];
}

export interface CatalogOption {
  id: string;
  fieldKey: string;
  value: string;
  label: string;
  sortOrder: number;
  isActive: boolean;
}

export interface InstitutionsSummary {
  public: number;
  private: number;
  unclassified: number;
  total: number;
}

export interface StatusFunnelDuration {
  status: string;
  averageDaysInStatus: number;
  currentTeamCount: number;
}

export interface IncompleteMemberDataRow {
  teamMemberId: string;
  teamId: string;
  teamName: string;
  memberName: string;
  missingFields: string[];
}

export interface InstitutionParticipationRow {
  editionId: string;
  editionName: string;
  newInstitutionsCount: number;
  recurrentInstitutionsCount: number;
  newInstitutions: string[];
  recurrentInstitutions: string[];
}

export interface TeamFunnelStats {
  statusFunnelDurations: StatusFunnelDuration[];
  incompleteMemberData: IncompleteMemberDataRow[];
  institutionParticipation: InstitutionParticipationRow[];
}

export interface AdminPayment {
  id: string;
  amount: number;
  planId: string;
  status: string;
  paymentMethod: PaymentMethod;
  bankReference: string | null;
  receiptUrl: string | null;
  notes: string | null;
  paidAt: string | null;
  confirmedAt: string | null;
  createdAt: string;
}

export interface AdminStatusLog {
  id: string;
  // cccd-team-category-change: 'status' (default, comportamiento histórico)
  // vs. 'category_change' — en este último previousStatus/newStatus guardan
  // el categoryId (slug) viejo/nuevo, no un CccdTeamStatus.
  logType: string;
  previousStatus: string;
  newStatus: string;
  note: string | null;
  discountSnapshot: { discountType: string; discountPercent: number } | null;
  createdAt: string;
  changedBy: { id: string; name: string };
}

export interface AdminEvent {
  id: string;
  name: string;
  slug: string;
  eventStartDate: string;
  eventEndDate: string;
  registrationStartDate: string;
  registrationDeadline: string;
  consentText: string;
  isActive: boolean;
  // cccd-team-showcase: cuál evento alimenta Competidores/Documentos en
  // Academy — a lo sumo un evento nacional puede tener esto en true.
  isFeaturedInAcademy: boolean;
  // cccd-event-attendance-tickets: activado por el admin para habilitar la
  // emisión/visualización de boletos de asistencia.
  attendeeTicketsEnabled: boolean;
  _count: { teams: number };
  statusCounts: { status: CccdTeamStatus; _count: number }[];
}

export interface AdminRegionalEvent {
  id: string;
  name: string;
  slug: string;
  region: string;
  description: string;
  eventStartDate: string;
  eventEndDate: string;
  registrationStartDate: string;
  registrationDeadline: string;
  teamPriceCop: number;
  consentText: string;
  categorySlug: string;
  seedbedId: string | null;
  isActive: boolean;
  // cccd-team-showcase: ídem AdminEvent.isFeaturedInAcademy, para regionales.
  isFeaturedInAcademy: boolean;
  // Ídem AdminEvent.attendeeTicketsEnabled, para regionales.
  attendeeTicketsEnabled: boolean;
  createdAt: string;
  _count: { teams: number };
  statusCounts: { status: CccdTeamStatus; _count: number }[];
}

export interface AdminRegionalEventDetail extends AdminRegionalEvent {
  teams: Array<{
    id: string;
    name: string;
    status: CccdTeamStatus;
    createdAt: string;
    // cccd-scoring-admin-to-gestion: ya estaba en la respuesta (el backend
    // usa `include`, no `select`, así que todos los escalares del equipo
    // viajan) — solo faltaba declararlo en este tipo.
    categoryId: string | null;
    captain: { id: string; name: string; email: string } | null;
    payments: AdminPayment[];
    _count: { members: number };
  }>;
}

export interface EventCategory {
  id: string;
  eventId: string | null;
  regionalId: string | null;
  slug: string;
  name: string;
  audience: string;
  description: string;
  basePriceCop: number;
  basePriceUsd: number | null;
  availableMotors: string;
  motorDescription: string;
  allowKitSelection: boolean;
  allowAltimeterSelection: boolean;
  defaultKit: boolean;
  defaultAltimeter: boolean;
  imageKey: string | null;
  imageUrl?: string | null;
  features: string[];
  specificBenefits: string[];
  isActive: boolean;
  orderIndex: number;
  seedbedId: string | null;
  // cccd-team-seedbed-roster-sync: cupo máximo de miembros por equipo de esta
  // categoría — null = sin límite.
  maxTeamSize: number | null;
  // cccd-scoring-admin-to-gestion: a lo sumo una categoría por evento puede
  // estar en vivo — la decide LaunchControlSection.
  isLive: boolean;
  createdAt: string;
  // Solo presente en la respuesta pública (GET /cccd/events/:id/categories),
  // no en las rutas admin de categoría.
  addons?: (Pick<CccdAddon, 'id' | 'name' | 'priceCop' | 'priceUsd' | 'sortOrder'> & { isDefault: boolean })[];
}

// isLive se administra exclusivamente desde LaunchControlSection (Puntaje),
// nunca desde el formulario de categoría.
export type EventCategoryInput = Omit<EventCategory, 'id' | 'eventId' | 'regionalId' | 'createdAt' | 'addons' | 'isLive'>;

export interface SeedbedOption {
  id: string;
  name: string;
  institution_name?: string;
}

export interface PodiumResult {
  id: string;
  eventId: string | null;
  regionalEventId: string | null;
  categorySlug: string;
  position: 1 | 2 | 3;
  discountPercent: number;
  usedByTeamId: string | null;
  usedAt: string | null;
  createdAt: string;
  event: { id: string; name: string } | null;
  regionalEvent: { id: string; name: string; region: string } | null;
  team: {
    id: string;
    name: string;
    captain: { id: string; name: string; email: string } | null;
    institution: { id: string; name: string } | null;
  };
}

export interface EventPlan {
  id: string;
  eventId: string | null;
  regionalId: string | null;
  slug: string;
  name: string;
  description: string;
  planType: 'full' | 'installments' | 'flexible';
  installmentsCount: number | null;
  minFlexibleMonths: number | null;
  maxFlexibleMonths: number | null;
  interestRatePercent: number;
  earlyBirdDiscountPercent: number;
  earlyBirdDeadlineMonth: number | null;
  isActive: boolean;
  orderIndex: number;
  createdAt: string;
}

export type EventPlanInput = Omit<EventPlan, 'id' | 'eventId' | 'regionalId' | 'createdAt'>;

export interface AdminEventDuplicateResult extends AdminEvent {
  categories: EventCategory[];
  plans: EventPlan[];
}

export interface SeedbedSuggestion {
  seedbedId: string;
  seedbedName: string | null;
  sourceEventName?: string;
  sourceRegionalName?: string;
}

function data<T>(res: { data: { data: T } }): T {
  return res.data.data;
}

export const cccdAdmin = {
  // Teams
  listTeams: (params?: Record<string, string | number>) =>
    api.get<{ data: { teams: AdminTeamListItem[]; total: number; page: number; limit: number } }>('/cccd/admin/teams', { params }).then(data),

  getTeam: (id: string) =>
    api.get<{ data: AdminTeamDetail }>(`/cccd/admin/teams/${id}`).then(data),

  createTeam: (body: Record<string, unknown>) =>
    api.post<{ data: AdminTeamDetail }>('/cccd/admin/teams', body).then(data),

  // cccd-teams-institutions-csv-import / csv-import-workflows-and-location-fields:
  // CSV bulk import, scoped to one national event (eventId) or one regional
  // event (regionalEventId) per request. Returns a jobId — poll
  // GET /import-jobs/:id (see lib/importJobs.ts) for the created/skipped result.
  bulkCreateTeams: (body: { eventId?: string; regionalEventId?: string; rows: BulkCreateTeamRow[] }) =>
    api.post<{ data: { jobId: string } }>('/cccd/admin/teams/bulk', body).then(data),

  updateTeam: (id: string, body: Record<string, unknown>) =>
    api.patch<{ data: AdminTeamDetail }>(`/cccd/admin/teams/${id}`, body).then(data),

  changeStatus: (id: string, body: { status: CccdTeamStatus; note?: string; rejectedReason?: string }) =>
    api.patch<{ data: AdminTeamDetail }>(`/cccd/admin/teams/${id}/status`, body).then(data),

  // Envío manual del boleto/QR de acceso por correo (Resend) — el staff decide
  // cuándo el equipo está listo para recibirlo, no es automático.
  sendTeamTickets: (id: string) =>
    api.post<{ data: { sent: number; skipped: number } }>(`/cccd/admin/teams/${id}/send-tickets`, {}).then(data),

  setMemberContact: (teamId: string, memberId: string, isPrimaryContact: boolean) =>
    api.patch<{ data: AdminTeamMember }>(`/cccd/admin/teams/${teamId}/members/${memberId}/contact`, { isPrimaryContact }).then(data),

  addMember: (teamId: string, body: AdminAddMemberInput) =>
    api.post<{ data: AdminTeamMember }>(`/cccd/admin/teams/${teamId}/members`, body).then(data),

  updateMember: (teamId: string, memberId: string, body: UpdateMemberInput) =>
    api.patch<{ data: AdminTeamMember }>(`/cccd/admin/teams/${teamId}/members/${memberId}`, body).then(data),

  // cccd-team-seedbed-roster-sync: también des-inscribe del grupo del
  // semillero de la categoría, del lado del servidor.
  removeMember: (teamId: string, memberId: string) =>
    api.delete<{ data: null }>(`/cccd/admin/teams/${teamId}/members/${memberId}`).then(data),

  searchStudents: (query: string) =>
    api.get<{ data: { candidates: StudentCandidate[] } }>('/cccd/admin/students/search', { params: { query } }).then(data),

  // Institutions
  getInstitutionsSummary: () =>
    api.get<{ data: InstitutionsSummary }>('/cccd/admin/institutions/summary').then(data),

  // gestion-role-based-stats-dashboards
  getTeamFunnelStats: () =>
    api.get<{ data: TeamFunnelStats }>('/cccd/admin/team-funnel-stats').then(data),

  // Catalog options
  listCatalogOptions: (fieldKey: string, includeInactive = false) =>
    api.get<{ data: CatalogOption[] }>('/cccd/admin/catalog-options', { params: { fieldKey, includeInactive: includeInactive || undefined } }).then(data),

  createCatalogOption: (body: { fieldKey: string; value: string; label: string; sortOrder?: number }) =>
    api.post<{ data: CatalogOption }>('/cccd/admin/catalog-options', body).then(data),

  updateCatalogOption: (id: string, body: Partial<{ label: string; sortOrder: number; isActive: boolean }>) =>
    api.put<{ data: CatalogOption }>(`/cccd/admin/catalog-options/${id}`, body).then(data),

  // Addon catalog (cccd-category-addons-and-pricing)
  listAddons: () =>
    api.get<{ data: CccdAddon[] }>('/cccd/admin/addons').then(data),

  createAddon: (body: { name: string; priceCop?: number; priceUsd?: number; sortOrder?: number; isActive?: boolean }) =>
    api.post<{ data: CccdAddon }>('/cccd/admin/addons', body).then(data),

  updateAddon: (id: string, body: Partial<{ name: string; priceCop: number; priceUsd: number; sortOrder: number; isActive: boolean }>) =>
    api.patch<{ data: CccdAddon }>(`/cccd/admin/addons/${id}`, body).then(data),

  toggleAddon: (id: string, isActive: boolean) =>
    api.patch<{ data: CccdAddon }>(`/cccd/admin/addons/${id}`, { isActive }).then(data),

  // Soft delete: no borra la fila (preserva los vínculos con categorías y el
  // historial de precios ya cobrados), solo la oculta del catálogo.
  deleteAddon: (id: string) =>
    api.delete(`/cccd/admin/addons/${id}`),

  // Category ↔ addon links
  getCategoryAddons: (eventId: string, categoryId: string) =>
    api.get<{ data: CategoryAddonLink[] }>(`/cccd/admin/events/${eventId}/categories/${categoryId}/addons`).then(data),

  enableCategoryAddon: (eventId: string, categoryId: string, addonId: string, isDefault = false) =>
    api.post<{ data: CategoryAddonLink }>(`/cccd/admin/events/${eventId}/categories/${categoryId}/addons`, { addonId, isDefault }).then(data),

  updateCategoryAddon: (eventId: string, categoryId: string, addonId: string, isDefault: boolean) =>
    api.patch<{ data: CategoryAddonLink }>(`/cccd/admin/events/${eventId}/categories/${categoryId}/addons/${addonId}`, { isDefault }).then(data),

  disableCategoryAddon: (eventId: string, categoryId: string, addonId: string) =>
    api.delete(`/cccd/admin/events/${eventId}/categories/${categoryId}/addons/${addonId}`),

  // Supply catalog (cccd-team-supply-catalog) — construcción/decoración,
  // separado del catálogo de addons de arriba.
  listCatalogItems: () =>
    api.get<{ data: CccdCatalogItem[] }>('/cccd/admin/catalog-items').then(data),

  createCatalogItem: (body: { name: string; description?: string; imageUrl?: string; priceCop?: number; category: CatalogItemCategory; sortOrder?: number; isActive?: boolean }) =>
    api.post<{ data: CccdCatalogItem }>('/cccd/admin/catalog-items', body).then(data),

  updateCatalogItem: (id: string, body: Partial<{ name: string; description: string; imageUrl: string | null; priceCop: number; category: CatalogItemCategory; sortOrder: number; isActive: boolean }>) =>
    api.patch<{ data: CccdCatalogItem }>(`/cccd/admin/catalog-items/${id}`, body).then(data),

  toggleCatalogItem: (id: string, isActive: boolean) =>
    api.patch<{ data: CccdCatalogItem }>(`/cccd/admin/catalog-items/${id}`, { isActive }).then(data),

  // Soft delete: no borra la fila, solo isActive: false (preserva
  // habilitaciones por evento y solicitudes ya creadas).
  deleteCatalogItem: (id: string) =>
    api.delete<{ data: CccdCatalogItem }>(`/cccd/admin/catalog-items/${id}`),

  // Event ↔ catalog item links
  getEventCatalogItems: (eventId: string) =>
    api.get<{ data: EventCatalogItemLink[] }>(`/cccd/admin/events/${eventId}/catalog-items`).then(data),

  enableEventCatalogItem: (eventId: string, catalogItemId: string) =>
    api.post<{ data: EventCatalogItemLink }>(`/cccd/admin/events/${eventId}/catalog-items`, { catalogItemId }).then(data),

  disableEventCatalogItem: (eventId: string, catalogItemId: string) =>
    api.delete(`/cccd/admin/events/${eventId}/catalog-items/${catalogItemId}`),

  // Supply requests (solicitudes de equipo, sin cobro)
  listSupplyRequests: (eventId?: string) =>
    api.get<{ data: CccdSupplyRequest[] }>('/cccd/admin/supply-requests', { params: { eventId } }).then(data),

  updateSupplyRequestStatus: (id: string, status: 'pending' | 'reviewed') =>
    api.patch<{ data: CccdSupplyRequest }>(`/cccd/admin/supply-requests/${id}`, { status }).then(data),

  // Payments
  registerPayment: (teamId: string, body: Record<string, unknown>) =>
    api.post<{ data: AdminPayment }>(`/cccd/admin/teams/${teamId}/payments`, body).then(data),

  // cccd-decouple-payment-from-activation: confirmar un pago ya no cambia el
  // estado del equipo, así que la respuesta ya no incluye `team`.
  confirmPayment: (paymentId: string) =>
    api.patch<{ data: { payment: AdminPayment } }>(`/cccd/admin/payments/${paymentId}/confirm`).then(data),

  rejectPayment: (paymentId: string, reason?: string) =>
    api.patch<{ data: AdminPayment }>(`/cccd/admin/payments/${paymentId}/reject`, { reason }).then(data),

  // Events
  listEvents: (params?: Record<string, string | number>) =>
    api
      .get<{ data: { events: AdminEvent[]; total: number; page: number; limit: number } }>('/cccd/admin/events', { params })
      .then(data),

  getEvent: (id: string) =>
    api.get<{ data: AdminEvent }>(`/cccd/admin/events/${id}`).then(data),

  createEvent: (body: Record<string, unknown>) =>
    api.post<{ data: AdminEvent }>('/cccd/admin/events', body).then(data),

  updateEvent: (id: string, body: Record<string, unknown>) =>
    api.patch<{ data: AdminEvent }>(`/cccd/admin/events/${id}`, body).then(data),

  duplicateEvent: (id: string, body: Record<string, unknown>) =>
    api.post<{ data: AdminEventDuplicateResult }>(`/cccd/admin/events/${id}/duplicate`, body).then(data),

  // cccd-event-attendance-tickets: al activar, el backend emite en bloque los
  // boletos de los integrantes de equipo que aún no tengan uno.
  toggleEventAttendeeTickets: (id: string, enabled: boolean) =>
    api
      .patch<{ data: AdminEvent & { ticketsIssued: number } }>(`/cccd/admin/events/${id}/attendee-tickets`, { enabled })
      .then(data),

  // Regional Events
  listRegionals: (params?: Record<string, string | number>) =>
    api
      .get<{ data: { regionals: AdminRegionalEvent[]; total: number; page: number; limit: number } }>('/cccd/admin/regionals', { params })
      .then(data),

  getRegional: (id: string) =>
    api.get<{ data: AdminRegionalEventDetail }>(`/cccd/admin/regionals/${id}`).then(data),

  createRegional: (body: Record<string, unknown>) =>
    api.post<{ data: AdminRegionalEvent }>('/cccd/admin/regionals', body).then(data),

  updateRegional: (id: string, body: Record<string, unknown>) =>
    api.patch<{ data: AdminRegionalEvent }>(`/cccd/admin/regionals/${id}`, body).then(data),

  duplicateRegional: (id: string, body: Record<string, unknown>) =>
    api.post<{ data: AdminRegionalEvent }>(`/cccd/admin/regionals/${id}/duplicate`, body).then(data),

  // Ídem toggleEventAttendeeTickets, para regionales.
  toggleRegionalAttendeeTickets: (id: string, enabled: boolean) =>
    api
      .patch<{ data: AdminRegionalEvent & { ticketsIssued: number } }>(`/cccd/admin/regionals/${id}/attendee-tickets`, { enabled })
      .then(data),

  suggestRegionalSeedbed: (region: string) =>
    api.get<{ data: SeedbedSuggestion | null }>('/cccd/admin/regionals/suggest-seedbed', { params: { region } }).then(data),

  // cccd-decouple-payment-from-activation: registrar el pago ya no activa el
  // equipo, así que la respuesta ya no incluye `team`.
  confirmRegionalPayment: (teamId: string, body: { bankReference?: string; receiptUrl?: string; notes?: string }) =>
    api.post<{ data: { payment: AdminPayment } }>(`/cccd/admin/regionals/teams/${teamId}/payments`, body).then(data),

  // Event Categories
  getEventCategories: (eventId: string) =>
    api.get<{ data: EventCategory[] }>(`/cccd/events/${eventId}/categories`).then(data),

  suggestCategorySeedbed: (slug: string) =>
    api.get<{ data: SeedbedSuggestion | null }>('/cccd/admin/categories/suggest-seedbed', { params: { slug } }).then(data),

  getCategoryImageUploadUrl: (body: { filename: string; content_type?: string }) =>
    api.post<{ data: { upload_url: string; key: string } }>('/cccd/admin/categories/image-upload-url', body).then(data),

  // cccd-team-showcase: parche del equipo y foto de integrante, admin-only.
  getTeamPatchUploadUrl: (teamId: string, body: { filename: string; content_type?: string }) =>
    api.post<{ data: { upload_url: string; key: string } }>(`/cccd/admin/teams/${teamId}/patch-upload-url`, body).then(data),

  updateTeamPatch: (teamId: string, key: string | null) =>
    api.patch<{ data: { id: string; patchImageKey: string | null } }>(`/cccd/admin/teams/${teamId}/patch`, { key }).then(data),

  getTeamGroupPhotoUploadUrl: (teamId: string, body: { filename: string; content_type?: string }) =>
    api.post<{ data: { upload_url: string; key: string } }>(`/cccd/admin/teams/${teamId}/group-photo-upload-url`, body).then(data),

  updateTeamGroupPhoto: (teamId: string, key: string | null) =>
    api
      .patch<{ data: { id: string; groupPhotoImageKey: string | null } }>(`/cccd/admin/teams/${teamId}/group-photo`, { key })
      .then(data),

  getMemberPhotoUploadUrl: (teamId: string, memberId: string, body: { filename: string; content_type?: string }) =>
    api
      .post<{ data: { upload_url: string; key: string } }>(`/cccd/admin/teams/${teamId}/members/${memberId}/photo-upload-url`, body)
      .then(data),

  updateMemberPhoto: (teamId: string, memberId: string, key: string | null) =>
    api
      .patch<{ data: { id: string; photoImageKey: string | null } }>(`/cccd/admin/teams/${teamId}/members/${memberId}/photo`, { key })
      .then(data),

  setTeamPublicProfile: (teamId: string, enabled: boolean) =>
    api
      .patch<{ data: { id: string; publicProfileEnabled: boolean } }>(`/cccd/admin/teams/${teamId}/public-profile`, { enabled })
      .then(data),

  setMemberPublicPhoto: (teamId: string, memberId: string, authorized: boolean) =>
    api
      .patch<{ data: { id: string; publicPhotoAuthorized: boolean } }>(
        `/cccd/admin/teams/${teamId}/members/${memberId}/public-photo`,
        { authorized },
      )
      .then(data),

  createEventCategory: (eventId: string, body: Partial<EventCategoryInput>) =>
    api.post<{ data: EventCategory }>(`/cccd/admin/events/${eventId}/categories`, body).then(data),

  updateEventCategory: (eventId: string, categoryId: string, body: Partial<EventCategoryInput>) =>
    api.patch<{ data: EventCategory }>(`/cccd/admin/events/${eventId}/categories/${categoryId}`, body).then(data),

  deleteEventCategory: (eventId: string, categoryId: string) =>
    api.delete(`/cccd/admin/events/${eventId}/categories/${categoryId}`),

  // Event Plans
  getEventPlans: (eventId: string) =>
    api.get<{ data: EventPlan[] }>(`/cccd/events/${eventId}/plans`).then(data),

  createEventPlan: (eventId: string, body: Partial<EventPlanInput>) =>
    api.post<{ data: EventPlan }>(`/cccd/admin/events/${eventId}/plans`, body).then(data),

  updateEventPlan: (eventId: string, planId: string, body: Partial<EventPlanInput>) =>
    api.patch<{ data: EventPlan }>(`/cccd/admin/events/${eventId}/plans/${planId}`, body).then(data),

  deleteEventPlan: (eventId: string, planId: string) =>
    api.delete(`/cccd/admin/events/${eventId}/plans/${planId}`),

  // Regional Categories (same shape as event categories, discriminated by regionalId)
  getRegionalCategories: (regionalId: string) =>
    api.get<{ data: EventCategory[] }>(`/cccd/regionals/${regionalId}/categories`).then(data),

  createRegionalCategory: (regionalId: string, body: Partial<EventCategoryInput>) =>
    api.post<{ data: EventCategory }>(`/cccd/admin/regionals/${regionalId}/categories`, body).then(data),

  updateRegionalCategory: (regionalId: string, categoryId: string, body: Partial<EventCategoryInput>) =>
    api.patch<{ data: EventCategory }>(`/cccd/admin/regionals/${regionalId}/categories/${categoryId}`, body).then(data),

  deleteRegionalCategory: (regionalId: string, categoryId: string) =>
    api.delete(`/cccd/admin/regionals/${regionalId}/categories/${categoryId}`),

  // Regional Plans
  getRegionalPlans: (regionalId: string) =>
    api.get<{ data: EventPlan[] }>(`/cccd/regionals/${regionalId}/plans`).then(data),

  createRegionalPlan: (regionalId: string, body: Partial<EventPlanInput>) =>
    api.post<{ data: EventPlan }>(`/cccd/admin/regionals/${regionalId}/plans`, body).then(data),

  updateRegionalPlan: (regionalId: string, planId: string, body: Partial<EventPlanInput>) =>
    api.patch<{ data: EventPlan }>(`/cccd/admin/regionals/${regionalId}/plans/${planId}`, body).then(data),

  deleteRegionalPlan: (regionalId: string, planId: string) =>
    api.delete(`/cccd/admin/regionals/${regionalId}/plans/${planId}`),

  // Seedbeds (para el selector de acceso a cursos)
  listSeedbedOptions: () =>
    api
      .get<{ data: { seedbeds: SeedbedOption[] } }>('/academy/seedbeds/admin', { params: { limit: 200, page: 1 } })
      .then((res) => res.data.data?.seedbeds ?? []),

  // Podium results
  getResults: (params: { eventId?: string; regionalEventId?: string; categorySlug?: string; onlyUnused?: boolean; search?: string }) =>
    api.get<{ data: { results: PodiumResult[] } }>('/cccd/admin/results', { params }).then(data),

  createEventResult: (eventId: string, body: { categorySlug: string; position: 1 | 2 | 3; teamId: string }) =>
    api.post<{ data: PodiumResult }>(`/cccd/admin/events/${eventId}/results`, body).then(data),

  createRegionalResult: (regionalId: string, body: { categorySlug: string; position: 1 | 2 | 3; teamId: string }) =>
    api.post<{ data: PodiumResult }>(`/cccd/admin/regionals/${regionalId}/results`, body).then(data),

  deleteResult: (resultId: string) =>
    api.delete(`/cccd/admin/results/${resultId}`),

  // Patrocinadores de un evento (puntos-sponsors-and-live-cache).
  listSponsors: (eventId: string) =>
    api.get<{ data: EventSponsor[] }>(`/cccd/admin/events/${eventId}/sponsors`).then(data),

  createSponsor: (eventId: string, body: EventSponsorInput) =>
    api.post<{ data: EventSponsor }>(`/cccd/admin/events/${eventId}/sponsors`, body).then(data),

  updateSponsor: (id: string, body: Partial<EventSponsorInput> & { isActive?: boolean; logoKey?: string | null }) =>
    api.patch<{ data: EventSponsor }>(`/cccd/admin/sponsors/${id}`, body).then(data),

  deleteSponsor: (id: string) => api.delete(`/cccd/admin/sponsors/${id}`),

  getSponsorLogoUploadUrl: (id: string, body: { filename: string; content_type: string }) =>
    api.post<{ data: { upload_url: string; key: string } }>(`/cccd/admin/sponsors/${id}/logo-upload-url`, body).then(data),

  applyPodiumDiscount: (teamId: string, resultId: string) =>
    api.post<{ data: AdminTeamDetail }>(`/cccd/admin/teams/${teamId}/apply-podium-discount`, { resultId }).then(data),
};
